import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import CTA from "../components/CTA";
import { INVESTMENTS as MOCK_INVESTMENTS } from "../mock";
import { ArrowUpRight, Flame, Loader2 } from "lucide-react";
import { useState, useMemo, useEffect } from "react";
import { getIcon } from "../lib/icons";
import { getOffers } from "../lib/api";

const CATS = ["All", "Property", "Fixed Income", "Equity", "Pension", "Tax-Advantaged", "Alternative"];

export default function Investments() {
  const [active, setActive] = useState("All");
  const [offers, setOffers] = useState(null);

  useEffect(() => {
    let alive = true;
    getOffers()
      .then((data) => alive && setOffers(data))
      .catch(() => alive && setOffers(MOCK_INVESTMENTS));
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => {
    if (!offers) return [];
    return active === "All" ? offers : offers.filter((i) => i.category === active);
  }, [active, offers]);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="relative bg-[#0d1a2e] text-white overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark opacity-30 pointer-events-none" />
        <div className="absolute -top-20 -right-32 w-[36rem] h-[36rem] glow-emerald pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-16 lg:pt-20 pb-20">
          <div className="inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.18em] text-[#16d196]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#16d196]" />
            11 live investments · UK-wide
          </div>
          <h1 className="mt-5 text-[42px] lg:text-[72px] font-extrabold leading-[1.02] tracking-[-0.025em] max-w-4xl">
            Every vetted UK offer,{" "}
            <span className="text-[#16d196]">in one hub.</span>
          </h1>
          <p className="mt-5 text-white/70 text-[17px] lg:text-[18px] max-w-2xl leading-relaxed">
            Filter by asset class. Each offer shows yield, minimum entry, term and risk
            band at a glance. Click through for the full due-diligence pack.
          </p>
        </div>
      </section>

      <section className="py-14 lg:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex flex-wrap gap-2 mb-10">
            {CATS.map((c) => (
              <button
                key={c}
                onClick={() => setActive(c)}
                className={`px-4 h-10 rounded-full text-[13.5px] font-bold border transition-colors ${
                  active === c
                    ? "bg-[#0d1a2e] text-white border-[#0d1a2e]"
                    : "bg-white text-[#0d1a2e]/70 border-[#0d1a2e]/15 hover:border-[#16d196] hover:text-[#0d1a2e]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {offers === null && (
              <div className="col-span-full flex justify-center py-16">
                <Loader2 className="w-6 h-6 text-[#16d196] animate-spin" />
              </div>
            )}
            {filtered.map((inv) => {
              const Icon = getIcon(inv.icon);
              return (
                <Link
                  key={inv.slug}
                  to={`/investments/${inv.slug}`}
                  className="group relative bg-white border border-[#0d1a2e]/10 rounded-2xl p-6 hover:border-[#16d196]/60 hover:shadow-[0_12px_36px_rgba(13,26,46,0.08)] transition-all"
                >
                  {inv.hot && (
                    <div className="absolute top-5 right-5 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-bold text-[#0d1a2e] bg-[#fde68a] rounded-full px-2 py-0.5">
                      <Flame className="w-3 h-3" strokeWidth={2.6} /> HOT
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-[#e3f7ee] flex items-center justify-center group-hover:bg-[#16d196] transition-colors">
                      <Icon className="w-5 h-5 text-[#0d1a2e]" strokeWidth={2.2} />
                    </div>
                    <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#16d196] font-bold">
                      {inv.category}
                    </div>
                  </div>
                  <h3 className="mt-5 text-[19px] font-extrabold text-[#0d1a2e] tracking-tight">
                    {inv.name}
                  </h3>
                  <p className="mt-1 text-[13.5px] text-[#0d1a2e]/60 leading-snug">{inv.tagline}</p>
                  <div className="mt-5 grid grid-cols-3 gap-3 pt-4 border-t border-[#0d1a2e]/8">
                    <Cell label="Yield" value={inv.yieldRange} />
                    <Cell label="From" value={inv.minEntry} />
                    <Cell label="Term" value={inv.term} />
                  </div>
                  <div className="mt-5 flex items-center justify-between">
                    <span className="text-[12px] text-[#0d1a2e]/55">
                      Risk · <span className="text-[#0d1a2e]/90 font-bold">{inv.risk}</span>
                    </span>
                    <span className="inline-flex items-center gap-1 text-[13px] font-bold text-[#0d1a2e] group-hover:text-[#16d196]">
                      View offer
                      <ArrowUpRight className="w-4 h-4" strokeWidth={2.4} />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <CTA />
      <Footer />
    </div>
  );
}

function Cell({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.12em] text-[#0d1a2e]/45 font-bold">
        {label}
      </div>
      <div className="text-[13px] font-extrabold text-[#0d1a2e] mt-0.5">{value}</div>
    </div>
  );
}
